import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search, Radio, Database, KanbanSquare } from 'lucide-react';
import { useAppData } from '../../contexts/AppDataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { useSuggestionWizard } from '../../contexts/SuggestionWizardContext';
import { useToast } from '../../hooks/useToast';
import { IdealCustomerProfile } from '../../types';
import { apiClient } from '../../lib/api';
import { SIDEBAR_WIDTH } from '../../constants/layout';
import { isPipelineViewActive } from '../../utils/adminView';
import { SearchModal } from './SearchModal';
import { CampaignModal } from './CampaignModal';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import {
  SidebarHeader,
  SidebarSearchButton,
  SidebarNavItem,
  SidebarBottomNav,
  SidebarOverlayPanel,
  SidebarTooltip,
  SidebarICPSection,
  SidebarDatasetSection,
  SidebarEmailSection,
  SidebarCreditsBox,
  EditICPModal,
  EditICPFormData,
  NavItem,
  useSidebarState,
} from './sidebar';

interface AppSidebarProps {
  selectedDatasetId?: string;
  defaultCollapsed?: boolean;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({
  selectedDatasetId,
  defaultCollapsed = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Shared with MobileDrawer + AnalyticsTabs so an invalid ?view= can't render one
  // tab while the nav highlights another.
  const isPipelineTabActive = isPipelineViewActive(location.pathname, location.search);
  const { openSettings, isOpen: isSettingsOpen } = useSettingsModal();
  const { openWizard, openWithIcp, isOpen: isWizardOpen } = useSuggestionWizard();
  const { showToast } = useToast();
  const { datasets: cachedDatasets, isLoadingDatasets, isLoadingMore, hasMoreDatasets, loadMoreDatasets, creditData, subscriptionData } = useAppData();

  const datasets = cachedDatasets || [];

  const { refs, state, actions } = useSidebarState({ defaultCollapsed });
  const prevWizardOpen = useRef(isWizardOpen);

  // Refresh ICP profiles when wizard closes (in case a new ICP was created)
  useEffect(() => {
    if (prevWizardOpen.current && !isWizardOpen) {
      actions.refreshICPProfiles();
    }
    prevWizardOpen.current = isWizardOpen;
  }, [isWizardOpen, actions]);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ isOpen: boolean; icp: IdealCustomerProfile | null }>({
    isOpen: false,
    icp: null,
  });

  const handleEditIcp = (e: React.MouseEvent, icp: IdealCustomerProfile) => {
    e.stopPropagation();
    actions.setEditingIcp(icp);
  };

  const handleSaveIcp = async (icp: IdealCustomerProfile, formData: EditICPFormData) => {
    actions.setIsSavingIcp(true);
    try {
      const updateData = {
        name: formData.name.trim(),
        solution_products: formData.solution_products.trim() || undefined,
        customer_type: formData.customer_type || undefined,
        selling_motion: formData.selling_motion || undefined,
        company_sizes: formData.company_sizes.length > 0 ? formData.company_sizes : undefined,
        target_verticals: formData.target_verticals.length > 0 ? formData.target_verticals : undefined,
        titles: formData.titles.trim() || undefined,
        target_region: formData.target_region.length > 0 ? formData.target_region : undefined,
      };
      await apiClient.updateICPProfile(icp.id, updateData);
      actions.setIcpProfiles(prev => prev.map(p =>
        p.id === icp.id ? { ...p, ...updateData } as IdealCustomerProfile : p
      ));
      actions.setEditingIcp(null);
    } catch (error) {
      console.error('Failed to update ICP:', error);
      showToast('Failed to update customer profile', 'error');
    } finally {
      actions.setIsSavingIcp(false);
    }
  };

  const handleDeleteIcp = (e: React.MouseEvent, icp: IdealCustomerProfile) => {
    e.stopPropagation();
    setDeleteConfirmation({ isOpen: true, icp });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmation.icp) return;
    
    try {
      await apiClient.deleteICPProfile(deleteConfirmation.icp.id);
      actions.setIcpProfiles(prev => prev.filter(p => p.id !== deleteConfirmation.icp!.id));
    } catch (error) {
      console.error('Failed to delete ICP:', error);
      showToast('Failed to delete customer profile', 'error');
    } finally {
      setDeleteConfirmation({ isOpen: false, icp: null });
    }
  };

  const navItems: NavItem[] = [
    {
      id: 'new-task',
      label: 'New Task',
      icon: Plus,
      path: '/datasets',
      isActive: state.isOnHome && !selectedDatasetId,
      dataTourHome: 'new-task',
    },
    // Operator-only surface: the board is a Command Center tab, so gate on admin and
    // match the tab (not the legacy /pipeline path, which only redirects here).
    ...(user?.isAdmin ? [{
      id: 'pipeline',
      label: 'Pipeline',
      icon: KanbanSquare,
      path: '/analytics?view=pipeline',
      isActive: isPipelineTabActive,
    }] : []),
    ...(state.isCollapsed ? [{
      id: 'search',
      label: 'Search',
      icon: Search,
      action: () => actions.setIsSearchModalOpen(true),
    }] : []),
    {
      id: 'monitors',
      label: 'Monitors',
      icon: Radio,
      action: () => openSettings('monitors'),
      dataTour: 'sidebar-signals',
    },
  ];

  const datasetsNavItem: NavItem = {
    id: 'datasets',
    label: 'Datasets',
    icon: Database,
    action: () => actions.toggleOverlay('datasets'),
    isActive: !!selectedDatasetId,
    badge: datasets.length > 0 ? datasets.length : undefined,
  };

  const getTooltipLabel = () => {
    if (!state.hoveredItem) return '';
    const allItems = [...navItems, datasetsNavItem];
    const item = allItems.find(i => i.id === state.hoveredItem);
    if (item) return item.label;
    if (state.hoveredItem === 'credits') return 'Credits';
    if (state.hoveredItem === 'settings') return 'Settings';
    if (state.hoveredItem === 'help') return 'Help';
    return '';
  };

  return (
    <>
      <div
        ref={refs.sidebarRef}
        className="hidden md:flex flex-col flex-shrink-0 h-full transition-[width] duration-200 ease-out relative z-[105] bg-neutral-100 border-r border-neutral-200"
        style={{ width: state.isCollapsed ? SIDEBAR_WIDTH.collapsed : SIDEBAR_WIDTH.expanded }}
        data-tour="sidebar-full"
      >
        <SidebarHeader
          isCollapsed={state.isCollapsed}
          onToggleCollapsed={() => actions.setIsCollapsed(!state.isCollapsed)}
        />

        {!state.isCollapsed && (
          <SidebarSearchButton onClick={() => actions.setIsSearchModalOpen(true)} />
        )}

        <div className={state.isCollapsed ? 'px-2 py-1' : 'px-3 py-1'}>
          {navItems.map(item => (
            <SidebarNavItem
              key={item.id}
              item={item}
              isCollapsed={state.isCollapsed}
              onNavigate={navigate}
              onMouseEnter={actions.handleMouseEnter}
              onMouseLeave={actions.handleMouseLeave}
            />
          ))}
          {state.isCollapsed && (
            <SidebarNavItem
              item={datasetsNavItem}
              isCollapsed={state.isCollapsed}
              onNavigate={navigate}
              onMouseEnter={actions.handleMouseEnter}
              onMouseLeave={actions.handleMouseLeave}
            />
          )}
        </div>

        {!state.isCollapsed && (
          <div className="flex-1 overflow-y-auto scrollbar-hide" style={{ minHeight: 0 }}>
            <SidebarICPSection
              icpProfiles={state.icpProfiles}
              datasets={datasets}
              isExpanded={state.isICPSectionExpanded}
              expandedIcpIds={state.expandedIcpIds}
              onToggleSection={() => actions.toggleSection('icp')}
              onToggleIcp={actions.toggleIcpExpanded}
              onOpenWizard={openWizard}
              onOpenWithIcp={openWithIcp}
              onEditIcp={handleEditIcp}
              onDeleteIcp={handleDeleteIcp}
            />

            <SidebarDatasetSection
              datasets={datasets}
              selectedDatasetId={selectedDatasetId}
              isExpanded={state.isDatasetsSectionExpanded}
              isLoading={isLoadingDatasets}
              isLoadingMore={isLoadingMore}
              hasMoreDatasets={hasMoreDatasets}
              searchQuery={state.searchQuery}
              onToggleSection={() => actions.toggleSection('datasets')}
              onLoadMore={loadMoreDatasets}
            />

            <SidebarEmailSection
              campaigns={state.sentCampaigns}
              isExpanded={state.isEmailSectionExpanded}
              isLoading={state.isLoadingCampaigns}
              onToggleSection={() => actions.toggleSection('email')}
              onOpenEmailSettings={() => openSettings('email')}
              onNewCampaign={() => setShowCampaignModal(true)}
            />
          </div>
        )}

        {state.isCollapsed && <div className="flex-1" />}

        <div className="mt-auto flex-shrink-0 border-t border-neutral-100">
          {creditData && (
            <SidebarCreditsBox
              credits={creditData.current_month_credits || 0}
              planName={subscriptionData?.plan_name || 'Free'}
              isCollapsed={state.isCollapsed}
              onOpenBilling={() => openSettings('billing')}
            />
          )}

          <SidebarBottomNav
            isCollapsed={state.isCollapsed}
            isSettingsActive={isSettingsOpen}
            onOpenSettings={() => openSettings('settings')}
            onOpenHelp={() => window.open('https://calendly.com/phillipan', '_blank')}
            onMouseEnter={actions.handleMouseEnter}
            onMouseLeave={actions.handleMouseLeave}
          />
        </div>
      </div>

      {state.showOverlay && state.isCollapsed && (
        <SidebarOverlayPanel
          ref={refs.overlayRef}
          datasets={datasets}
          selectedDatasetId={selectedDatasetId}
          isLoading={isLoadingDatasets}
          isLoadingMore={isLoadingMore}
          hasMoreDatasets={hasMoreDatasets}
          searchQuery={state.searchQuery}
          onClose={actions.closeOverlay}
          onOpenSearch={() => {
            actions.closeOverlay();
            actions.setIsSearchModalOpen(true);
          }}
          onLoadMore={loadMoreDatasets}
        />
      )}

      {state.isCollapsed && state.hoveredItem && !state.showOverlay && (
        <SidebarTooltip label={getTooltipLabel()} position={state.tooltipPosition} />
      )}

      <SearchModal
        isOpen={state.isSearchModalOpen}
        onClose={() => actions.setIsSearchModalOpen(false)}
        datasets={datasets}
        onNewTask={() => navigate('/datasets')}
      />

      {state.editingIcp && (
        <EditICPModal
          icp={state.editingIcp}
          isOpen={true}
          onClose={() => actions.setEditingIcp(null)}
          onSave={handleSaveIcp}
          isSaving={state.isSavingIcp}
        />
      )}

      <CampaignModal
        isOpen={showCampaignModal}
        onClose={() => setShowCampaignModal(false)}
        datasets={datasets}
        onNewCampaign={() => {
          setShowCampaignModal(false);
          navigate('/datasets');
        }}
      />

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, icp: null })}
        onConfirm={handleConfirmDelete}
        title="Delete ICP Profile"
        message={`Are you sure you want to delete "${deleteConfirmation.icp?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      <style>{`
        @keyframes sidebarOverlayIn {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </>
  );
};
